import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import userProgressReducer from "../features/userProgress/userProgressSlice";
import { pedagogueApi, postgresApi } from "./api";
import scoreTrackingReducer from "./scoreTrackingSlice";

export const store = configureStore({
  reducer: {
    userProgress: userProgressReducer,
    scoreTracking: scoreTrackingReducer,
    [pedagogueApi.reducerPath]: pedagogueApi.reducer,
    [postgresApi.reducerPath]: postgresApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      pedagogueApi.middleware,
      postgresApi.middleware
    ),
  devTools: true,
});

// Required for the refetchOnFocus / refetchOnReconnect flags on postgresApi to
// do anything — RTK Query only wires the browser focus/online events up when
// setupListeners has been called.
setupListeners(store.dispatch);

// Hard cache reset when the deployed build changes.
//
// refetchOnFocus converges an open session on the NEXT focus event; this covers
// the other case — a returning session whose cached payload predates a deploy
// that changed course structure (the 2026-07-24 "Leading with AI" split turned
// one 6-level course into two 3-level courses). Keyed on the same
// REACT_APP_DEPLOY_ID the image cache-buster uses (utils/imageUrl.ts), so it
// fires exactly once per deploy and is a no-op in local builds where the
// variable is unset.
const DEPLOY_ID_STORAGE_KEY = "pedagogue.deployId";
const CURRENT_DEPLOY_ID = process.env.REACT_APP_DEPLOY_ID || "";
if (CURRENT_DEPLOY_ID && typeof window !== "undefined") {
  try {
    const seen = window.localStorage.getItem(DEPLOY_ID_STORAGE_KEY);
    if (seen !== CURRENT_DEPLOY_ID) {
      window.localStorage.setItem(DEPLOY_ID_STORAGE_KEY, CURRENT_DEPLOY_ID);
      if (seen) {
        // Only reset on an actual CHANGE. A first-ever visit has nothing stale.
        store.dispatch(postgresApi.util.resetApiState());
      }
    }
  } catch {
    // Private browsing / storage disabled — refetchOnFocus still applies.
  }
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
